import { useMemo } from 'react';

import { cn } from '@/lib/cn';
import { diffStats, lineDiff } from '@/lib/line-diff';

interface DiffViewerProps {
  before: string;
  after: string;
  beforeLabel?: string;
  afterLabel?: string;
}

/**
 * Side-by-side line diff for skill prompts. Aligns rows by LCS so equal
 * lines stay visually paired and inserted/removed lines show on a single
 * side with a coloured background.
 */
export function DiffViewer({
  before,
  after,
  beforeLabel = 'Active',
  afterLabel = 'Draft',
}: DiffViewerProps) {
  const rows = useMemo(() => lineDiff(before, after), [before, after]);
  const stats = diffStats(rows);

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-2 py-1 text-small">
        <div className="flex gap-3">
          <span className="font-medium text-slate-700">{beforeLabel}</span>
          <span className="text-slate-400">→</span>
          <span className="font-medium text-slate-700">{afterLabel}</span>
        </div>
        <div className="flex gap-2 font-mono">
          <span className="text-emerald-600">+{stats.added}</span>
          <span className="text-red-600">-{stats.removed}</span>
        </div>
      </div>

      <div className="grid flex-1 grid-cols-2 overflow-auto text-small">
        <pre className="border-r border-slate-200 bg-white font-mono">
          {rows.map((row, idx) => (
            <Row
              key={`L-${idx}`}
              side="left"
              kind={row.kind}
              lineNo={row.leftLine}
              text={row.left}
            />
          ))}
        </pre>
        <pre className="bg-white font-mono">
          {rows.map((row, idx) => (
            <Row
              key={`R-${idx}`}
              side="right"
              kind={row.kind}
              lineNo={row.rightLine}
              text={row.right}
            />
          ))}
        </pre>
      </div>
    </div>
  );
}

function Row({
  side,
  kind,
  lineNo,
  text,
}: {
  side: 'left' | 'right';
  kind: 'equal' | 'del' | 'add';
  lineNo: number | null;
  text: string | null;
}) {
  const isFilled = text !== null;
  const isActive =
    (side === 'left' && kind === 'del') || (side === 'right' && kind === 'add');
  return (
    <div
      className={cn(
        'flex min-h-[1.25rem] gap-2 px-2 leading-5',
        isActive && side === 'left' && 'bg-red-50',
        isActive && side === 'right' && 'bg-emerald-50',
        !isFilled && 'bg-slate-50/60',
      )}
    >
      <span className="w-8 select-none text-right text-slate-400">{lineNo ?? ''}</span>
      <span className="flex-1 whitespace-pre-wrap break-words text-slate-700">
        {text ?? ' '}
      </span>
    </div>
  );
}
